"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Link, useNavigate } from "react-router-dom"
import { useApp } from "../../contexts/AppContext"
import { BiPlus, BiPencil, BiTrash, BiCopy, BiToggleLeft, BiToggleRight, BiData, BiCog } from "react-icons/bi"

import FeuilleModal from "../modals/FeuilleModal"
import ConfirmAlert from "../alerts/ConfirmAlert"
import DropdownOption from "../ui/DropdownOption"

export default function FeuilleCard({ feuilles, classeurId }) {
  const navigate = useNavigate()
  const { deleteFeuilleAPI, updateFeuilleAPI, getFeuilleDataAPI, addFeuilleAPI, loading } = useApp()

  // États pour les modals
  const [feuillesData, setFeuillesData] = useState({})
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [editingFeuilleId, setEditingFeuilleId] = useState(null)
  const [deletingFeuilleId, setDeletingFeuilleId] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      const results = {}
      for (const feuille of feuilles) {
        try {
          const data = await getFeuilleDataAPI(feuille.code_feuille)
          results[feuille.code_feuille] = data
        } catch (err) {
          console.error("Erreur chargement données feuille", feuille.code_feuille, err)
          results[feuille.code_feuille] = []
        }
      }
      setFeuillesData(results)
    }

    if (feuilles.length > 0) fetchData()
  }, [feuilles])

  const handleDeleteFeuille = async () => {
    if (deletingFeuilleId) {
      try {
        await deleteFeuilleAPI(deletingFeuilleId)
      } catch (error) {
        console.error("Erreur lors de la suppression:", error)
      }
    }
  }

  const handleEditFeuille = (feuilleId) => {
    setEditingFeuilleId(feuilleId)
    setShowEditModal(true)
  }

  const handleDuplicateFeuille = async (feuille) => {
    try {
      const duplicatedFeuille = {
        ...feuille,
        nom_feuille: `${feuille.nom_feuille} (Copie)`,
        libelle_feuille: `Copie de ${feuille.libelle_feuille || feuille.nom_feuille}`,
        date_insertion: new Date().toISOString(),
      }
      delete duplicatedFeuille.code_feuille
      await addFeuilleAPI(duplicatedFeuille)
    } catch (error) {
      console.error("Erreur lors de la duplication:", error)
    }
  }

  const handleToggleStatus = async (feuille) => {
    try {
      const updatedFeuille = {
        ...feuille,
        statut: feuille.statut === 1 ? 0 : 1,
      }
      await updateFeuilleAPI(feuille.code_feuille, updatedFeuille)
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error)
    }
  }

  const handleCloseEditModal = () => {
    setShowEditModal(false)
    setEditingFeuilleId(null)
  }

  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirm(false)
    setDeletingFeuilleId(null)
  }

  // Variants d'animation
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 10, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
    hover: {
      y: -2,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10,
      },
    },
  }

  return (
    <>
      <motion.div className="row" variants={containerVariants} initial="hidden" animate="visible">
        <AnimatePresence>
          {feuilles.map((feuille, index) => (
            <motion.div
              key={feuille.code_feuille}
              className="col-md-6 col-lg-4 col-xl-3 mb-4"
              variants={cardVariants}
              whileHover="hover"
              style={{ zIndex: feuilles.length - index }}
            >
              <div className="card h-100 shadow-sm border rounded-1">
                {/* En-tête de la carte */}
                <div className="card-header bg-light border-bottom d-flex justify-content-between align-items-center p-1">
                  <div className="d-flex align-items-center">
                    {feuille.icone && <i className={`bi bi-${feuille.icone} me-2 text-primary`}></i>}
                    <h6 className="card-title mb-0 text-truncate small">{feuille.nom_feuille}</h6>
                  </div>
                  <DropdownOption
                    options={[
                      {
                        label: "Modifier",
                        icon: <BiPencil />,
                        onClick: () => handleEditFeuille(feuille.code_feuille),
                        className: "text-primary",
                      },
                      {
                        label: "Dupliquer",
                        icon: <BiCopy />,
                        onClick: () => handleDuplicateFeuille(feuille),
                        className: "text-success",
                      },
                      {
                        label: feuille.statut === 1 ? "Désactiver" : "Activer",
                        icon: feuille.statut === 1 ? <BiToggleLeft /> : <BiToggleRight />,
                        onClick: () => handleToggleStatus(feuille),
                        className: feuille.statut === 1 ? "text-warning" : "text-success",
                      },
                      {
                        label: "Supprimer",
                        icon: <BiTrash />,
                        onClick: () => {
                          setDeletingFeuilleId(feuille.code_feuille)
                          setShowDeleteConfirm(true)
                        },
                        className: "text-danger",
                      },
                    ]}
                  />
                </div>

                {/* Corps de la carte */}
                <div className="card-body">
                  {feuille.libelle_feuille && (
                    <p className="card-text text-muted small mb-3">
                      {feuille.libelle_feuille.length > 100
                        ? `${feuille.libelle_feuille.substring(0, 100)}...`
                        : feuille.libelle_feuille}
                    </p>
                  )}

                  {/* Statistiques */}
                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <div className="d-flex gap-1 align-items-center text-center p-2 bg-light rounded-1">
                        <BiCog className="text-success d-block fs-6" />
                        <small className="text-muted d-block">
                          Colonnes: <strong className="small">{feuille.colonnes?.length || 0}</strong></small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="d-flex gap-1 align-items-center text-center p-2 bg-light rounded-1">
                        <BiData className="text-primary d-block fs-6" />
                        <small className="text-muted d-block">
                          Données: <strong className="small">{feuillesData[feuille.code_feuille]?.length || 0}</strong></small>
                      </div>
                    </div>
                  </div>

                  {/* Badges */}
                  <div className="d-flex justify-content-between align-items-center">
                    <span className={`badge rounded-1 ${feuille.statut === 1 ? "bg-success" : "bg-secondary"}`}>
                      {feuille.statut === 1 ? "Active" : "Inactive"}
                    </span>
                    <small className="text-muted">
                      Ajouter: {feuille.date_insertion ? new Date(feuille.date_insertion).toLocaleDateString("fr-FR") : "-"}
                    </small>
                  </div>
                </div>

                {/* Pied de carte */}
                <div className="card-footer bg-transparent border-top p-1">
                  <div className="d-flex gap-2">
                    <button onClick={() => navigate(`/feuille/${feuille.code_feuille}/data`)}
                      className="btn btn-sm btn-light flex-grow-1 d-flex align-items-center justify-content-center" >
                      <BiPlus className="me-1" /> Insérer des données
                    </button>

                    <button onClick={() => navigate(`/feuille/${feuille.code_feuille}/fields`)}
                      className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                      title="Configurer les champs" >
                      <BiCog />
                    </button>
                  </div>
                </div>

                {/* Indicateur de chargement */}
                {loading && (
                  <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75 rounded">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Modals */}
      <FeuilleModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        classeurId={classeurId}
        feuilleId={editingFeuilleId}
      />

      <ConfirmAlert
        isOpen={showDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        onConfirm={handleDeleteFeuille}
        title="Supprimer la feuille"
        message={`Êtes-vous sûr de vouloir supprimer la feuille "${feuilles.find((f) => f.code_feuille === deletingFeuilleId)?.nom_feuille
          }" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        variant="danger"
      />
    </>
  )
}
